import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { TagReference } from "../../models";
import { useData } from "../../utils/useData";
import { useDynamicEmployeeValidationSchema } from "../../utils/useDynamicValidationSchema";
import { PersonalDetailsForm } from "./PersonalDetailsForm";
import { TagSelection } from "./TagSelection";
import { SideDrawerTitle } from "../shared/SideDrawer";
import { X } from "react-feather";
import { useCreateUser } from "../../hooks/users/useCreateUser";

type AddUserProps = {
  handleClose: () => void;
};

export type CreateEmployeeFormValuesType = {
  customerEmployeeId: string;
  firstName: string;
  lastName: string;
  contact: {
    email: string;
    phone: string;
  };
};

export const AddUser = ({ handleClose }: AddUserProps) => {
  const { allEmployees, employer } = useData();

  const [isConfirmCloseModalOpen, setIsConfirmCloseModalOpen] = useState(false);
  const [sendInvitationEmail, setSendInvitationEmail] = useState(true);
  const [tags, setTags] = useState<TagReference[]>([]);

  const [error, setError] = useState("");
  const initialValues: CreateEmployeeFormValuesType = {
    customerEmployeeId: "",
    firstName: "",
    lastName: "",
    contact: {
      email: "",
      phone: ""
    }
  };

  const addEmployeeMutation = useCreateUser({
    onSuccess: () => handleClose(),
    onError: (error) => setError(error.message),
  });

  const validationSchema = useDynamicEmployeeValidationSchema(
    employer.data?.country.id ?? "",
    allEmployees.data?.items
  );

  const { resetForm, ...formik } = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      addEmployeeMutation.mutate({formValues: values, tags, sendInvitationEmail});
    },
    enableReinitialize: true
  });

  // Reset form when the modal is opened
  useEffect(() => {
      resetForm();
      setTags([]);
      setError("");
  }, [resetForm]);

  return (
    <>
        <form onSubmit={formik.handleSubmit}>
          <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
            <SideDrawerTitle>Add new user</SideDrawerTitle>
            <IconButton       
              sx={{ 
                marginTop: 2,
                marginRight: 2,
                marginLeft: "auto"
              }}
              onClick={() => setIsConfirmCloseModalOpen(true)}
            >
              <X/>
            </IconButton>
          </Box>
          <Box padding={2}>
            <Grid container spacing={2}>
              {error && (
                <Grid item xs={12}>
                  <Typography color="error">{error}</Typography>
                </Grid>
              )}
              <Grid container spacing={2} item xs={12}>
                <PersonalDetailsForm formik={formik} allowEmailChange={true} />
              </Grid>
              <Grid container item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked
                      value={sendInvitationEmail}
                      onChange={(e) => setSendInvitationEmail(e.target.checked)}
                    />
                  }
                  label="Send invitation email"
                />
              </Grid>
              <Grid container spacing={1} item xs={12}>
                <Grid item xs={12}>
                  <Typography variant="h6">Tags</Typography>
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    value={formik.values.customerEmployeeId}
                    onChange={formik.handleChange}
                    label="User Id"
                    name="customerEmployeeId"
                    fullWidth
                    size="small"
                    error={
                      formik.touched.customerEmployeeId &&
                      Boolean(formik.initialValues.customerEmployeeId)
                    }
                    helperText={
                      formik.touched.customerEmployeeId &&
                      formik.errors.customerEmployeeId
                    }
                  />
                </Grid>
                <TagSelection tags={tags} setTags={setTags} />
              </Grid>
            </Grid>
          </Box>
          <Box padding={2}>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              color="primary"
              loading={addEmployeeMutation.isLoading || employer.isLoading}
            >
              Save
            </LoadingButton>
          </Box>
        </form>
      {isConfirmCloseModalOpen && (
        <Dialog open={isConfirmCloseModalOpen} maxWidth="xs" fullWidth={true}>
          <DialogContent>
            <Typography>Are you sure you want to exit?</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsConfirmCloseModalOpen(false)}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setIsConfirmCloseModalOpen(false);
                handleClose();
              }}
              color="primary"
            >
              Exit
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
